import { useCallback, useEffect, useState } from "react";
import { Loader2, LogOut } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { staffApi } from "@/components/staff/api";
import { SignIn } from "@/components/staff/SignIn";
import { DownloadsPanel } from "@/components/staff/DownloadsPanel";
import { SecurityPanel } from "@/components/staff/SecurityPanel";
import { ActivityPanel } from "@/components/staff/ActivityPanel";
import { RecordsPanel } from "@/components/staff/RecordsPanel";
import { StaffPanel } from "@/components/staff/StaffPanel";
import { NewsAdminPanel } from "@/components/staff/news/NewsAdminPanel";

const TABS = [
  { k: "downloads", l: "Downloads" },
  { k: "news", l: "News & Events", admin: true },
  { k: "activity", l: "Download History", admin: true },
  { k: "records", l: "Records", admin: true },
  { k: "staff", l: "Staff Accounts", admin: true },
  { k: "security", l: "My Security" },
];

const Dashboard = ({ user, notice, onRefresh, onSignOut }) => {
  const [tab, setTab] = useState("downloads");
  const [meta, setMeta] = useState(null);
  const isAdmin = user?.role === "admin";
  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => {
    if (!isAdmin) return;
    staffApi.get("/admin/meta").then((r) => setMeta(r.data ?? null)).catch(() => setMeta(null));
  }, [isAdmin]);
  return (
    <section className="min-h-[90svh] bg-paper pb-24 text-ink" data-testid="staff-dashboard">
      <Seo title="Staff Area | First Responders Foundation" description="Staff-only page." path="/staff" noindex />
      <div className="bg-navy pb-8 pt-32 text-paper">
        <div className="container-x flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">Staff Area</p>
            <h1 className="mt-2 text-3xl font-extrabold text-paper md:text-4xl" data-testid="staff-dashboard-name">{user?.name}</h1>
            <p className="mt-1 text-sm text-[#C9D4DF]" data-testid="staff-dashboard-role">{user?.role_label}</p>
          </div>
          <button type="button" onClick={onSignOut} className="btn btn-ghost-light" data-testid="staff-signout-button"><LogOut size={16} /> Sign Out</button>
        </div>
      </div>
      <div className="container-x">
        {notice && <p role="status" className="mt-6 border-l-4 border-gold bg-white p-4 text-sm text-ink" data-testid="staff-dashboard-notice">{notice}</p>}
        <nav className="mt-6 flex gap-1 overflow-x-auto" aria-label="Staff sections">
          {TABS.filter((t) => !t.admin || isAdmin).map((t) => (
            <button key={t.k} type="button" onClick={() => setTab(t.k)} aria-current={tab === t.k ? "page" : undefined}
              className={`whitespace-nowrap px-4 py-3 text-sm font-semibold transition-colors ${tab === t.k ? "bg-navy text-paper" : "text-navy hover:bg-mist"}`}
              data-testid={`staff-tab-${t.k}`}>{t.l}</button>
          ))}
        </nav>
        <div className="mt-8">
          {tab === "downloads" && <DownloadsPanel />}
          {tab === "security" && <SecurityPanel user={user} onChange={onRefresh} />}
          {tab === "activity" && isAdmin && <ActivityPanel />}
          {tab === "records" && isAdmin && (meta ? <RecordsPanel lists={meta.lists} /> : <Loader2 className="animate-spin text-navy" />)}
          {tab === "staff" && isAdmin && <StaffPanel meta={meta} me={user} />}
          {tab === "news" && isAdmin && <NewsAdminPanel />}
        </div>
      </div>
    </section>
  );
};

export default function Staff() {
  const [user, setUser] = useState(null);
  const [checked, setChecked] = useState(false);
  const [notice, setNotice] = useState("");
  const refresh = useCallback(() => {
    staffApi.get("/auth/me").then((r) => setUser(r.data ?? null)).catch(() => setUser(null)).finally(() => setChecked(true));
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const signedIn = (u, usedRecovery) => {
    setUser(u ?? null);
    setNotice(usedRecovery
      ? `You signed in with a recovery code. ${u?.recovery_left ?? 0} codes left. If you replaced your phone, ask the Administrator to reset your two-step sign-in, then make new recovery codes.`
      : "");
  };
  const signOut = async () => {
    try { await staffApi.post("/auth/logout"); } finally { setUser(null); setNotice(""); }
  };

  if (!checked) return <section className="min-h-[90svh] bg-navy pt-36" aria-busy="true" />;
  if (!user) return <SignIn onSignedIn={signedIn} />;
  return <Dashboard user={user} notice={notice} onRefresh={refresh} onSignOut={signOut} />;
}
